import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Timestamp } from '@firebase/firestore';
import { isEqual, orderBy } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { combineLatestWith, fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, map, take, takeUntil } from 'rxjs/operators';
import { Service, ServiceTypes } from '../../../models';
import { DialogService, ServicesService } from '../../../services';

@Component({
  selector: 'prism-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss']
})
export class ConfirmationComponent implements OnInit, OnDestroy {
  @ViewChild('history') historyTemplate: ElementRef<HTMLInputElement>;
  @ViewChild('placeholder') placeholderTemplate: ElementRef<HTMLInputElement>;
  @ViewChild('productsTemplate') productsTemplate: ElementRef<HTMLInputElement>;

  serviceTabActive: boolean;
  subServices: SubService[];
  filteredSubServices: SubService[];
  confirmation: SubServiceConfirmation;
  confirmationCopy: SubServiceConfirmation;
  invoiceConfirmationTotal: number;
  invoiceHistoryTotal: number;
  serviceType: 'imageService' | 'videoService' | 'floorplanService';
  subDestroyer$: Subject<void> = new Subject();
  service: Service;
  postingConfirmation: boolean;
  confirmationChanged: boolean;
  priorityFilters: Priority[];
  selectedPriorityFilter: Priority;
  searchString: string;
  confirmationsHistory: {
    created: string,
    processorName: string,
    confirmations: {
      subServiceType: string,
      itemQuantity: number
      itemTotal: number
    }[]
  }[];
  confirmationsLoading$: Observable<boolean>;
  subServicesLoading$: Observable<boolean>;

  @ViewChild('searchAddressInput') searchAddressInput: ElementRef<HTMLInputElement>;

  constructor(private dialogService: DialogService,
              private servicesService: ServicesService
  ) {
    this.serviceTabActive = true;
    this.invoiceConfirmationTotal = null;
    this.invoiceHistoryTotal = null;
    this.confirmationsHistory = [];
    this.confirmation = {};
    this.selectedPriorityFilter = 'All';
    this.postingConfirmation = false;
    this.confirmationChanged = false;
    this.confirmationsLoading$ = this.servicesService.isGetConfirmationsLoading$;
    this.subServicesLoading$ = this.servicesService.isGetSubServiceLoading$;
  }

  ngOnInit() {
    this.serviceType = this.dialogService.inputData.serviceType;
    this.service = this.dialogService.inputData.job;
    this.priorityFilters = ['All', 'Major', 'Minor'];
    this.servicesService.getSubServices()
      .pipe(combineLatestWith(this.servicesService.getConfirmations(this.service.id)))
      .subscribe(([subServices, confirmationHistory]) => {
        this.populateConfirmationsList(subServices, confirmationHistory);
        this.setupSearchInputListener();
      });
  }

  populateConfirmationsList(subServices: SubService[], confirmationHistory: {
    subServiceId: string;
    subServiceType: ServiceTypes;
    unixTimestamp: number;
    processorName: string;
    itemQuantity: string;
    productCode: string;
    serviceInvoiced?: boolean;
  }[]) {
    this.subServices = orderBy(subServices[this.serviceType], ['priority'], ['asc']);
    this.filteredSubServices = this.subServices;
    confirmationHistory.forEach((confirmation: {
      subServiceId: string,
      subServiceType: ServiceTypes,
      unixTimestamp: number,
      processorName: string,
      itemQuantity: string,
      productCode: string,
      serviceInvoiced?: boolean
    }) => {
      const {subServiceId, subServiceType, unixTimestamp, processorName, itemQuantity, productCode} = confirmation;
      const createdMoment = moment(unixTimestamp);
      const daysDiff = createdMoment.diff(moment(), 'days');
      const created = (daysDiff === 0) ? moment(unixTimestamp).fromNow() : createdMoment.format('D MMMM YYYY');

      const subsServiceIndex = this.subServices.findIndex((item) => {
        return subServiceId === item.id;
      });
      const totalValue = (parseInt(itemQuantity, 10) * parseFloat(this.subServices[subsServiceIndex].purchasesUnitPrice))

      if (confirmation.serviceInvoiced) {
        const confirmationHistoryIndex = this.confirmationsHistory.findIndex((confirmation) => {
          return confirmation.created === created;
        });

        if (subsServiceIndex > -1 && this.subServices[subsServiceIndex].purchasesUnitPrice) {
          this.invoiceHistoryTotal += totalValue;
        }

        if (confirmationHistoryIndex > -1) {
          this.confirmationsHistory[confirmationHistoryIndex].confirmations.push({
            subServiceType,
            itemQuantity: parseInt(itemQuantity, 10),
            itemTotal: totalValue
          });
        } else {
          this.confirmationsHistory.push(
            {
              created,
              processorName: processorName || 'No Name Available',
              confirmations: [{
                subServiceType,
                itemQuantity: parseInt(itemQuantity, 10),
                itemTotal: totalValue
              }]
            }
          );
        }

      } else {
        if (this.confirmation[subServiceId]) {
          this.confirmation[subServiceId].itemCount += parseInt(itemQuantity, 10);
        } else {
          this.confirmation[subServiceId] = {
            itemCount: parseInt(itemQuantity, 10),
            itemCode: productCode
          };
        }

        if (subsServiceIndex > -1 && this.subServices[subsServiceIndex].purchasesUnitPrice) {
          this.invoiceConfirmationTotal += totalValue;
        }
      }
    });
    this.confirmationCopy = cloneDeep(this.confirmation);
    this.servicesService.switchGetSubServicesLoaderOff();
    this.servicesService.switchGetConfirmationsLoaderOff();
  }

  setupSearchInputListener() {
    setTimeout(() => {
      fromEvent(this.searchAddressInput.nativeElement, 'keyup')
        .pipe(
          takeUntil(this.subDestroyer$),
          map((e: KeyboardEvent) => (e.target as HTMLInputElement).value),
          debounceTime(0)
        )
        .subscribe(() => {
          this.searchString = this.searchAddressInput.nativeElement.value;
          this.filterSubServices(this.searchString, this.selectedPriorityFilter);
        });
    }, 0);
  }

  filterSubServices(search: string, priority: Priority) {
    if (priority === this.priorityFilters[0]) {
      if (search) {
        this.filteredSubServices = this.subServices.filter((subService: SubService) => {
          return (subService.itemName.toLowerCase()).startsWith(search?.toLowerCase());
        });
      } else {
        this.filteredSubServices = this.subServices;
      }
    } else {
      this.filteredSubServices = this.subServices.filter((subService: SubService) => {
        if (priority === this.priorityFilters[2]) {
          return subService.priority !== 'major';
        }
        return subService.priority === priority.toLowerCase();
      });
      if (search) {
        this.filteredSubServices = this.filteredSubServices.filter((subService: SubService) => {
          return (subService.itemName.toLowerCase()).startsWith(search?.toLowerCase());
        });
      }
    }
  }

  closeDialog() {
    this.dialogService.closeDialog();
  }

  confirmJob() {
    this.postingConfirmation = true;
    this.servicesService.postConfirmation(this.service, this.confirmation).pipe(take(1))
      .subscribe(() => {
        const confirmationIds = Object.keys(this.confirmation);
        this.postingConfirmation = false;
        this.dialogService.closeDialog(confirmationIds);
      }, (err) => {
        this.postingConfirmation = false;
        console.error(err);
      });
  }

  increaseSubServiceCount(subServiceId: string) {
    this.confirmation[subServiceId].itemCount++;
    const subsServiceIndex = this.subServices.findIndex((item) => {
      return subServiceId === item.id;
    });
    if (subsServiceIndex > -1 && this.subServices[subsServiceIndex].purchasesUnitPrice) {
      this.invoiceConfirmationTotal = parseFloat((parseFloat(this.subServices[subsServiceIndex].purchasesUnitPrice) + this.invoiceConfirmationTotal).toFixed(2));
    }
    this.checkConfirmationChange();
  }

  decreaseSubServiceCount(subServiceId: string) {
    this.confirmation[subServiceId].itemCount = this.confirmation[subServiceId].itemCount - 1;
    const subsServiceIndex = this.subServices.findIndex((item) => {
      return subServiceId === item.id;
    });

    if (subsServiceIndex > -1 && this.subServices[subsServiceIndex].purchasesUnitPrice) {
      this.invoiceConfirmationTotal = parseFloat((this.invoiceConfirmationTotal - parseFloat(this.subServices[subsServiceIndex].purchasesUnitPrice)).toFixed(2));
    }
    this.checkConfirmationChange();
  }

  subServiceChange(subService, checked) {
    const index = this.subServices.findIndex((item) => {
      return item.id === subService.id;
    });
    const selectedSubService = this.subServices[index];

    selectedSubService.selected = checked;

    if (this.confirmation[subService.id]) {
      const subsServiceIndex = this.subServices.findIndex((item) => {
        return subService.id === item.id;
      });

      if (subsServiceIndex > -1 && this.subServices[subsServiceIndex].purchasesUnitPrice) {
        const subServiceCost = parseFloat((parseFloat(this.subServices[subsServiceIndex].purchasesUnitPrice) * this.confirmation[subService.id].itemCount).toFixed(2));
        this.invoiceConfirmationTotal = parseFloat((this.invoiceConfirmationTotal - subServiceCost).toFixed(2));
      }

      delete this.confirmation[subService.id];

    } else {
      const subsServiceIndex = this.subServices.findIndex((item) => {
        return subService.id === item.id;
      });

      if (subsServiceIndex > -1 && this.subServices[subsServiceIndex].purchasesUnitPrice) {
        if (this.invoiceConfirmationTotal === null) {
          this.invoiceConfirmationTotal = 0;
        }
        this.invoiceConfirmationTotal = parseFloat((this.invoiceConfirmationTotal + parseFloat(this.subServices[subsServiceIndex].purchasesUnitPrice)).toFixed(2));
      }

      this.confirmation[subService.id] = {
        itemCount: 1,
        itemCode: selectedSubService.itemCode
      };
    }
    this.checkConfirmationChange();
  }

  onServicePriorityFilterChange(value: Priority) {
    this.selectedPriorityFilter = value;
    this.filterSubServices(this.searchString, this.selectedPriorityFilter);
  }

  displayHistory() {
    if (this.confirmationsHistory.length > 0) {
      this.serviceTabActive = false;
    }
  }

  displayServices() {
    this.serviceTabActive = true;
  }

  checkConfirmationChange() {
    this.confirmationChanged = !isEqual(this.confirmation, this.confirmationCopy);
  }

  ngOnDestroy() {
    this.subDestroyer$.next(null);
  }

  protected readonly history = history;
}

interface SubServiceConfirmation {
  [id: string]: {
    itemCode: string;
    itemCount: number;
  };
}

type Priority = 'All' | 'Major' | 'Minor'

export interface SubService {
  id: string;
  itemCode: string;
  itemName: string;
  createdAt: Timestamp;
  purchasesUnitPrice?: string;
  active?: boolean;
  serviceType?: ServiceTypes;
  selected?: boolean;
  priority: 'major' | 'minor';
}
